import {
  confirmSignIn as amplifyConfirmSignIn,
  signIn as amplifySignIn,
  signOut as amplifySignOut,
  fetchAuthSession,
  getCurrentUser,
} from 'aws-amplify/auth'
import SecureLS from 'secure-ls'

// Amplify.configure() の副作用を先に走らせる。
// これより前に fetchAuthSession を呼ぶと「Auth UserPool not configured」になる。
import '~/components/Utils/Auth'

/**
 * Cognito 認証のラッパ。
 *
 * 画面からは必ずここを通す。Amplify の API を直接呼ぶと、
 * App.Token（下記）の同期が漏れて古い実装の認証チェックとズレる。
 */

// 旧実装（Laravel Sanctum）は SecureLS の 'App.Token' にトークンを入れ、
// 各画面が「値があればログイン済み」と判断していた。その判定を残したまま
// 中身だけ Cognito の ID トークンに差し替える。
//
// ⚠️ ここに入っている値は「最後に取得した時点の」ID トークンで、1 時間で失効する。
//    実際に API へ送るトークンは lib/apiClient.ts が毎リクエスト取り直すので、
//    この値はあくまでログイン状態の目印として扱うこと。
const storage = new SecureLS({ encodingType: 'aes', encryptionNamespace: 'canaly' })
const APP_TOKEN_KEY = 'App.Token'

function rememberToken(token: string | null): void {
  try {
    if (token) storage.set(APP_TOKEN_KEY, token)
    else storage.remove(APP_TOKEN_KEY)
  } catch {
    // ストレージが使えない環境でも認証自体は続行させる。
  }
}

export async function signIn(email: string, password: string) {
  return amplifySignIn({ username: email, password })
}

/** 新パスワード設定・TOTP セットアップ確認・TOTP コード入力すべてに使う。 */
export async function confirmSignIn(response: string) {
  return amplifyConfirmSignIn({ challengeResponse: response })
}

export async function signOut() {
  rememberToken(null)
  return amplifySignOut()
}

/** 現在の ID トークン。期限が近ければ Amplify が裏で更新する。 */
export async function getIdToken(): Promise<string | null> {
  try {
    const session = await fetchAuthSession()
    const token = session.tokens?.idToken?.toString() ?? null
    rememberToken(token)
    return token
  } catch {
    rememberToken(null)
    return null
  }
}

export async function getUser() {
  try {
    return await getCurrentUser()
  } catch {
    return null
  }
}

/**
 * cognito:groups を配列に正規化する。
 * ID トークンでは配列で入っているが、環境によっては文字列で来ることがあるので両対応にする。
 */
function normalizeGroups(raw: unknown): string[] {
  if (Array.isArray(raw)) return raw.map((value) => String(value)).filter(Boolean)
  if (typeof raw === 'string') {
    return raw
      .replace(/^\[/, '')
      .replace(/\]$/, '')
      .split(/[\s,]+/)
      .filter(Boolean)
  }
  return []
}

export type UserClaims = {
  email: string | null
  groups: string[]
}

export async function getUserClaims(options?: { forceRefresh?: boolean }): Promise<UserClaims | null> {
  try {
    const session = await fetchAuthSession({ forceRefresh: options?.forceRefresh })
    const claims = session.tokens?.idToken?.payload
    if (!claims) {
      rememberToken(null)
      return null
    }
    rememberToken(session.tokens?.idToken?.toString() ?? null)
    return {
      email: typeof claims.email === 'string' ? claims.email : null,
      groups: normalizeGroups(claims['cognito:groups']),
    }
  } catch {
    rememberToken(null)
    return null
  }
}

export function isAdmin(claims: UserClaims | null): boolean {
  return claims?.groups.includes('admin') ?? false
}
